import { type SyncedRolodexFetchId } from 'features/rolodex/extensionFunctions/entriesWithSyncedRefreshes/internal/providers/createRefreshSyncRegistry'
import { getRefreshSyncScopeFromFetch } from 'features/rolodex/extensionFunctions/entriesWithSyncedRefreshes/internal/refreshSyncScope/getRefreshSyncScope'
import { useRolodexEntries } from 'features/rolodex/hooks/useRolodexEntries'
import React from 'react'

import { useRolodexRefreshSyncRegistry } from './RolodexEntriesWithSyncedRefreshesProvider'

// MAINTAINER-CONTRACT.IfChange:
// This hook's public API, provider requirement, registration timing, or
// returned forceRefresh behavior changes.
// MAINTAINER-CONTRACT.ThenChange(src/features/rolodex/extensionFunctions/entriesWithSyncedRefreshes/README.md)
// MAINTAINER-CONTRACT.ThenChange(src/features/rolodex/extensionFunctions/entriesWithSyncedRefreshes/internal/UseRolodexEntriesWithSyncedRefreshes.test.tsx)

export function useRolodexEntriesWithSyncedRefreshes(
  queryOrQueries: UseRolodexEntriesWithSyncedRefreshesQuery,
  options?: UseRolodexEntriesWithSyncedRefreshesOptions
): UseRolodexEntriesWithSyncedRefreshesResult {
  const result = useRolodexEntries(queryOrQueries, options)

  const refreshSyncScope = React.useMemo(
    () =>
      getRefreshSyncScopeFromFetch({
        entries: result.entries,
        queryOrQueries,
      }),
    [queryOrQueries, result.entries]
  )

  const refreshSyncRegistry = useRolodexRefreshSyncRegistry()
  const syncedFetchId = React.useRef<SyncedRolodexFetchId>(
    Symbol('rolodexEntriesWithSyncedRefreshesInstance')
  ).current
  const rawForceRefresh = result.forceRefresh

  // Register before caller layout effects so same-commit refreshes can sync.
  React.useInsertionEffect(() => {
    return refreshSyncRegistry.register({
      id: syncedFetchId,
      refreshSyncScope,
      rawForceRefresh,
    })
  }, [rawForceRefresh, refreshSyncScope, refreshSyncRegistry, syncedFetchId])

  const syncedForceRefresh = React.useCallback(async () => {
    await refreshSyncRegistry.refreshOverlappingFetches(syncedFetchId)
  }, [refreshSyncRegistry, syncedFetchId])

  return {
    ...result,
    forceRefresh: syncedForceRefresh,
  }
}

// ----------------------------------------------------------------------------
//                                Helper Types
// ----------------------------------------------------------------------------

export type UseRolodexEntriesWithSyncedRefreshesQuery = Parameters<
  typeof useRolodexEntries
>[0]

export type UseRolodexEntriesWithSyncedRefreshesOptions = Parameters<
  typeof useRolodexEntries
>[1]

export type UseRolodexEntriesWithSyncedRefreshesResult = ReturnType<
  typeof useRolodexEntries
>
